// Packages
import { Fragment, useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { useTranslation } from "react-i18next";
import MailCheck from "react-mailcheck";
import DOMPurify from "dompurify";
import Select from "react-select";
import { ArrowLeft, UserCircle } from "@phosphor-icons/react";

// Components
import LoadingIcon from "../components/LoadingIcon";
import AddressForm from "../components/AddressForm";
import CustomFieldList from "./CustomFieldList";
import Header from "./Header";
import Alert from "./Alert";
import NavigationFooter from "./NavigationFooter";
import ShowOrderSummary from "./ShowOrderSummary";
import InformationValidation from "../validations/InformationValidation";
import ValidateForm from "../validations/ValidateForm";
import addCustomFieldsValidation from "../validations/addCustomFieldsValidation";
import PhoneInput from "./custom_fields/PhoneInput/PhoneInput";
import {
  getCountryPhonePrefix,
  isPhoneNumberValid,
  getPhonePrefixCountry,
} from "./custom_fields/PhoneInput/helpers";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MobileDiscountForm from "./MobileDiscountForm";

// Actions
import { closeAlertMessage } from "../redux/ui";
import {
  saveInformation,
  saveShippingOption,
  setFieldInformation,
  setFieldBilling,
  updateCountry,
  updateRegion,
  setFieldShipping,
  updateLocations,
  setCustomFieldInformation,
  setData,
  loadedPage,
  fetchDataInformation,
  saveEmail,
  savePhone,
  callAddressAutofill,
  fetchShippingTypeBranches,
  fetchAddressForBranchAddress,
  fetchRegionMunicipalityCoordinates,
} from "../actions/checkoutStore";
import { useNavigate } from "react-router-dom";
import PickupLocationsMap from "./PickupLocationsMap";
import PickupPointsMap from "./PickupPointsMap";
import AddressSearchInput from "./AddressSearchInput";
import StripeExpressCheckout from "./StripeExpressCheckout";

const InformationContent = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [errorDiv, setErrorDiv] = useState(null);
  const [infoDiv, setInfoDiv] = useState(null);

  const isFirstRun = useRef(true);
  const [clickedButton, setClickedButton] = useState(false);

  const containerRef = useRef(null);
  const branchRefs = useRef({});

  const { information, shipping, summary, urls, system_fields, colors } =
    useSelector((state) => state.checkoutStore);
  // TODO: Remove/fix? Does customers store ever exist?
  const storeCountry = useSelector(
    (state) => state.customersStore?.storeCountry,
  );

  const [phoneError, setPhoneError] = useState({ value: false, message: "" });
  const [currentPhone, setCurrentPhone] = useState(
    (information.phone != null && information.phone) || "",
  );
  const [contentExceedsMaxHeight, setContentExceedsMaxHeight] = useState(false);
  const [currentPrefix, setCurrentPrefix] = useState(
    information.phone_prefix != ""
      ? information.phone_prefix
      : information.country != ""
        ? getCountryPhonePrefix("", information.country)
        : information.order_country != ""
          ? getCountryPhonePrefix("", information.order_country)
          : "",
  );
  const [loadedShippingBranches, setLoadedShippingBranches] = useState([]);
  const [pickupMissingProductsOpen, setPickupMissingProductsOpen] = useState(
    [],
  );

  const { doingRequest, alertMessage } = useSelector((state) => state.ui);

  const borderColor = "#424242";

  const defaultColor = "#929292";
  const defaultColorBorder = `1px solid ${defaultColor}`;
  const mainColorBorder = `1px solid ${borderColor}`;

  useEffect(() => {
    setLoadedShippingBranches(shipping.branches);
  }, [shipping.branches]);

  useEffect(() => {
    validatePhone();
  }, [currentPrefix, currentPhone]);

  const validatePhone = () => {
    let phone = currentPhone == null ? "" : currentPhone;
    let prefix =
      currentPrefix == null
        ? getCountryPhonePrefix("", storeCountry)
        : currentPrefix;
    let isValidPhone = isPhoneNumberValid(phone, prefix);

    if (!isValidPhone) {
      setPhoneError({ value: true, message: t("information.phone-not-valid") });
    } else {
      setPhoneError({ value: false, message: "" });
      changeStoreField("phone", currentPhone);
      changeStoreField("phone_prefix", currentPrefix);
      dispatch(
        savePhone(urls.save_phone, {
          phone: currentPhone,
          phone_prefix: currentPrefix,
        }),
      );
    }
  };

  useEffect(() => {
    if (isFirstRun.current) {
      dispatch(loadedPage("checkout"));
      window.scrollTo(0, 0);
      isFirstRun.current = false;
      return;
    }
  }, []);

  useEffect(() => {
    if (!information.no_shipping_required) {
      if (
        !information.has_shipping_methods &&
        shipping.pickup_points.length > 0
      ) {
        changeShippingOption("shipping_option", "store_pickup");
      } else if (
        information.has_shipping_methods &&
        shipping.pickup_points.length == 0
      ) {
        changeShippingOption("shipping_option", "delivery");
      }
    }
  }, [information.has_shipping_methods, shipping.pickup_points]);

  useEffect(() => {
    const pickUpContainer = document.getElementById("pickUpContainer");
    if (
      pickUpContainer &&
      pickUpContainer.scrollHeight > pickUpContainer.clientHeight
    ) {
      setContentExceedsMaxHeight(true);
    } else {
      setContentExceedsMaxHeight(false);
    }
  }, [shipping.pickup_point_id, information.shipping_option]);

  useEffect(() => {
    if (errorDiv != null && (information.error != "" || shipping.error != ""))
      errorDiv.scrollIntoView();
  }, [errorDiv]);

  useEffect(() => {
    if (infoDiv != null && information.info_msg != "") infoDiv.scrollIntoView();
  }, [infoDiv]);

  useEffect(() => {
    if (clickedButton === true) {
      let errorsList = Object.keys(information.errors);
      if (errorsList.length > 0) {
        let currentElement = document.getElementById(errorsList[0]);
        if (currentElement != null) {
          currentElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          currentElement.focus();
        }
      }
      setClickedButton(false);
      return;
    }
  }, [information.errors]);

  useEffect(() => {
    if (
      shipping.branch_id &&
      branchRefs.current[shipping.branch_id] &&
      containerRef.current
    ) {
      const element = branchRefs.current[shipping.branch_id];
      const container = containerRef.current;

      const elementOffsetTop = element.offsetTop - container.offsetTop;
      const elementOffsetLeft = element.offsetLeft - container.offsetLeft;

      container.scrollTo({
        top:
          elementOffsetTop -
          container.clientHeight / 2 +
          element.clientHeight / 2,
        left:
          elementOffsetLeft -
          container.clientWidth / 2 +
          element.clientWidth / 2,
        behavior: "smooth",
      });
    }
  }, [shipping.branch_id]);

  const errorMsgCallback = useCallback((node) => {
    if (node !== null) {
      setErrorDiv(node);
    }
  }, []);

  const setErrors = (errors) => {
    dispatch(setFieldInformation("errors", errors));
  };

  const changePickupPointFields = (pickup_point_id, pickup_point_name) => {
    dispatch(setFieldShipping("pickup_point_id", pickup_point_id));
    dispatch(setFieldShipping("pickup_point_name", pickup_point_name));
  };

  const changeBranchFields = (
    branch_id,
    branch_name,
    branch_address,
    id,
    branch,
  ) => {
    const prices_per_service = branch.service.map((s) => ({
      service_id: s.id,
      price: "",
    }));
    dispatch(setFieldShipping("selected_branch", branch));
    dispatch(setFieldShipping("prices_per_service", prices_per_service));
    dispatch(setFieldShipping("marker_id", id));
    dispatch(setFieldShipping("branch_id", String(branch_id)));
    dispatch(setFieldShipping("branch_name", branch_name));
    dispatch(setFieldShipping("shipping_branch_address", branch_address));
    dispatch(setFieldInformation("address", branch_address));
    dispatch(
      setFieldShipping("shipping_ids", {
        shipping_service_id: branch.service[0].id,
        shipping_method_id: branch.service[0].shipping_method_id,
        rate_id: shipping.shipping_ids.rate_id,
      }),
    );

    dispatch(
      setFieldShipping("shipping_names", {
        shipping_method_name: branch.shipping_method_name,
        shipping_service_name: branch.service[0].name,
      }),
    );
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,8}$/i;
    return emailRegex.test(email);
  };

  const dispatchSaveEmail = (email) => {
    if (isValidEmail(email)) {
      const params = {
        email: email,
      };
      dispatch(saveEmail(urls.save_email, params));
    }
  };

  const changeShippingOption = (field, value) => {
    // Check if there is a shipping method already defined
    if (
      shipping &&
      shipping.shipping_ids &&
      shipping.shipping_ids.shipping_method_id &&
      value === "store_pickup"
    ) {
      dispatch(saveShippingOption(urls.save_shipping_option, value));
    } else {
      dispatch(setFieldInformation(field, value));
    }
    const fulfillingPickupPoint = shipping.pickup_points.find(
      (point) => point.location_can_fulfill_order === true,
    );
    if (fulfillingPickupPoint && shipping.pickup_point_id == null) {
      dispatch(setFieldShipping("pickup_point_id", fulfillingPickupPoint.id));
      dispatch(
        setFieldShipping("pickup_point_name", fulfillingPickupPoint.name),
      );
    }
  };

  const changeStoreField = (field, value) => {
    dispatch(setFieldInformation(field, value));

    if (field == "shipping_option") {
      setErrors({});
      changeStoreField("error", "");
    } else if (field != "error") {
      let newErrors = Object.assign({}, information.errors);
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const setCustomField = (elementId, custom_field_id, value, area) => {
    dispatch(setCustomFieldInformation(custom_field_id, value, area));
    let newErrors = Object.assign({}, information.errors);
    delete newErrors[elementId];
    setErrors(newErrors);
  };

  const callUpdateLocations = (
    country,
    region,
    municipality,
    city,
    address = null,
    postal = null,
  ) => {
    if (doingRequest) {
      return;
    }
    let newErrors = Object.assign({}, information.errors);
    delete newErrors.country;
    delete newErrors.region;
    delete newErrors.municipality;
    delete newErrors.city;
    delete newErrors.address;
    delete newErrors.postal;
    setErrors(newErrors);

    const url = urls.update_locations;
    const params = {
      country: country,
      region: region,
      municipality: municipality,
      city: city,
      address: address,
      postal: postal,
      shipping: true,
    };
    dispatch(updateLocations(url, params));
  };

  const callUpdateCountry = (value) => {
    if (doingRequest) {
      return;
    }
    let newErrors = Object.assign({}, information.errors);
    delete newErrors.country;
    setErrors(newErrors);
    const url = urls.update_country;
    const params = {
      country: value,
      shipping: true,
    };
    if (currentPhone == "") {
      setCurrentPrefix(getCountryPhonePrefix("", value));
    }
    dispatch(updateCountry(url, params));
  };

  const callUpdateRegion = (value) => {
    if (doingRequest) {
      return;
    }
    let newErrors = Object.assign({}, information.errors);
    delete newErrors.region;
    setErrors(newErrors);
    const url = urls.update_region;
    const params = {
      country: information.country,
      region: value,
      shipping: true,
    };

    dispatch(updateRegion(url, params));
  };

  const addressAutofill = (country, postal) => {
    if (doingRequest) {
      return;
    }
    const url = urls.address_autofill;
    const params = {
      country: country,
      postal: postal,
      type: "information",
    };
    dispatch(callAddressAutofill(url, params));
  };

  const validate = async () => {
    let validationData;
    let allCustomFields;
    const shippingCustomFields = information.custom_fields.shipping;
    const otherCustomFields = information.custom_fields.other;
    const contactCustomFields = information.custom_fields.contact;

    validationData = Object.assign({}, information, system_fields);

    if (
      information.no_shipping_required &&
      information.show_billing_address_on_digital_products == "1"
    ) {
      allCustomFields = otherCustomFields.concat(contactCustomFields);
    } else {
      allCustomFields = shippingCustomFields
        .concat(otherCustomFields)
        .concat(contactCustomFields);
    }
    const { validations, updatedData } = addCustomFieldsValidation(
      allCustomFields,
      validationData,
      InformationValidation,
    );
    let results = ValidateForm(validations, updatedData);
    let emailValidationResults = await axios.get(urls.validate_email, {
      params: {
        email: information.email,
      },
    });

    if (!emailValidationResults.data.email_valid) {
      results = {
        errors: Object.assign({}, results.errors, {
          email: t(emailValidationResults.data.email_validation_message),
        }),
        status: false,
      };
    }

    setErrors(results.errors);

    return results.status;
  };

  const saveData = async () => {
    setClickedButton(true);
    const result = await validate();

    if (doingRequest || !result) {
      return;
    }

    setErrors({});
    changeStoreField("error", "");

    var url = "";
    var params = {};
    if (information.no_shipping_required) {
      url = urls.save_information;
      params = {
        information: {
          shipping_option: information.shipping_option,
          email: information.email,
          name: information.name,
          surname: information.surname,
          country: information.country,
          custom_fields: information.custom_fields,
          accepts_marketing: information.accepts_marketing,
          additional_information: information.additional_information,
        },
      };
    } else {
      if (information.shipping_option === "delivery") {
        url = urls.save_information;
        params = {
          information: {
            shipping_option: information.shipping_option,
            name: information.name,
            surname: information.surname,
            email: information.email,
            phone: information.phone,
            phone_prefix: information.phone_prefix,
            address: information.address,
            city: information.city,
            postal: information.postal,
            country: information.country,
            region: information.region,
            municipality: information.municipality,
            custom_fields: information.custom_fields,
            accepts_marketing: information.accepts_marketing,
            complement: information.complement,
            neighbourhood: information.neighbourhood,
            additional_information: information.additional_information,
            billing_same_as_shipping: information.billing_same_as_shipping,
          },
        };
      } else if (information.shipping_option === "store_pickup") {
        url = urls.save_info_pickup;

        params = {
          information: {
            shipping_option: information.shipping_option,
            email: information.email,
            name: information.name,
            surname: information.surname,
            phone: information.phone,
            phone_prefix: information.phone_prefix,
            custom_fields: information.custom_fields,
            accepts_marketing: information.accepts_marketing,
            additional_information: information.additional_information,
          },
        };
      } else if (information.shipping_option === "point_pickup") {
        url = urls.save_info_point_pickup;
        params = {
          information: {
            shipping_option: information.shipping_option,
            email: information.email,
            name: information.name,
            surname: information.surname,
            phone: information.phone,
            phone_prefix: information.phone_prefix,
            custom_fields: information.custom_fields,
            accepts_marketing: information.accepts_marketing,
            additional_information: information.additional_information,
            municipality: information.municipality,
            region: information.region,
            address: shipping.shipping_branch_address,
            country: shipping.selected_branch?.country,
            postal: shipping.selected_branch?.postal,
          },
        };
      }
    }

    if (
      shipping.pickup_points &&
      shipping.pickup_points.length > 0 &&
      information.shipping_option === "store_pickup"
    ) {
      params = {
        ...params,
        shipping: {
          pickup_point_id: shipping.pickup_point_id,
          pickup_point_name: shipping.pickup_point_name,
          name: information.name,
          surname: information.surname,
        },
      };
    }
    if (information.shipping_option === "point_pickup") {
      params = {
        ...params,
        shipping: {
          branch_id: shipping.branch_id,
          branch_name: shipping.branch_name,
          shipping_method_id: shipping.shipping_ids.shipping_method_id,
          shipping_service_id: shipping.shipping_ids.shipping_service_id,
          name: information.name,
          surname: information.surname,
          municipality: information.municipality,
          region: information.region,
          address: shipping.shipping_branch_address,
          country: information.country,
        },
      };
    }
    const queryParams = new URLSearchParams(window.location.search);
    const previewMode = queryParams.get("preview_mode") || "false";
    dispatch(saveInformation(url, params, previewMode, navigate));
  };

  const [loginFormOpen, setLoginFormOpen] = useState(false);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginAlertMessage, setLoginAlertMessage] = useState("");
  const [resetPasswordInfoMessage, setResetPasswordInfoMessage] = useState("");
  const [resetPasswordErrorMessage, setResetPasswordErrorMessage] =
    useState("");
  const [serviceOptions, setServiceOptions] = useState([]);

  const pickupLocationsHaveCoordinates = shipping.pickup_points?.every(
    (point) =>
      point.address &&
      point.address.latitude != null &&
      point.address.longitude != null &&
      point.address.latitude !== 0 &&
      point.address.longitude !== 0,
  );

  const customStyles = (isError) => ({
    control: (base, state) => ({
      ...base,
      borderRadius: "6px",
      height: "44px",
      padding: "0 8px",
      fontSize: "14px",
      border: isError ? "2px solid #ed2c2c" : "1px solid #ccc",
      backgroundColor: state.isDisabled
        ? "#f5f5f5"
        : isError
          ? "#feeded"
          : "white",
      opacity: state.isDisabled ? 0.6 : 1,
      cursor: state.isDisabled ? "not-allowed" : "default",
    }),

    placeholder: (base, state) => ({
      ...base,
      color: defaultColor,
      opacity: state.isDisabled ? 0.5 : 1,
    }),

    dropdownIndicator: (base, state) => ({
      ...base,
      color: defaultColor,
      padding: "8px",
      opacity: state.isDisabled ? 0.4 : 1,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),

    indicatorSeparator: () => ({ display: "none" }),

    valueContainer: (base) => ({
      ...base,
      padding: "4px 8px",
    }),

    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? defaultColor
        : state.isFocused
          ? "#f0f0f0"
          : "#fff",
      color: state.isSelected ? colors.text_color : "#000",
      padding: "8px 12px",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.5 : 1,
    }),
  });

  const handleKeyDown = (event, radioId) => {
    if (event.code === "Space") {
      event.preventDefault();
      const radio = document.getElementById(radioId);
      if (radio) {
        radio.click();
      }
    }
  };

  function changeService(value) {
    const service = shipping.selected_branch?.service.find(
      (s) => s.id == value,
    );
    dispatch(
      setFieldShipping("shipping_ids", {
        shipping_service_id: service.id,
        shipping_method_id: shipping.shipping_ids.shipping_method_id,
        rate_id: shipping.shipping_ids.rate_id,
      }),
    );
    dispatch(
      setFieldShipping("shipping_names", {
        shipping_method_name: shipping.selected_branch.shipping_method_name,
        shipping_service_name: service.name,
      }),
    );
  }

  useEffect(() => {
    if (shipping.selected_branch?.service?.length) {
      const options = shipping.selected_branch.service.map((service) => ({
        label: service.name,
        value: service.id,
        original: service,
      }));
      setServiceOptions(options);
    } else {
      setServiceOptions([]);
    }
  }, [shipping.prices_per_service]);

  useEffect(() => {
    if (information.shipping_option === "point_pickup") {
      const allowedTypes = [
        "starken",
        "bluexpress",
        "correos_chile",
        "dpd",
        "ctt",
      ];

      dispatch(
        fetchRegionMunicipalityCoordinates(
          information.region,
          information.municipality,
          information.country,
        ),
      );

      shipping.available_shipping_method_types.forEach((type) => {
        if (allowedTypes.includes(type)) {
          dispatch(
            fetchShippingTypeBranches(
              type,
              information.region,
              information.municipality,
            ),
          );
        }
      });
    }
  }, [information.municipality, information.shipping_option]);

  const selectedValue =
    serviceOptions.find(
      (opt) => opt.value === shipping.shipping_ids?.shipping_service_id,
    ) || null;

  const RenderLoginForm = () => {
    return (
      <Fragment>
        <div className="separatedContent col-12 ps-0">
          <div className="label-checkout" style={{ paddingBottom: "24px" }}>
            {t("information.login-to-your-account")}
          </div>
          <small
            className="label-checkout"
            onClick={() => {
              setLoginFormOpen(false);
              setPasswordFormOpen(true);
            }}
            style={{ cursor: "pointer" }}
          >
            {t("information.forgot-password")}
          </small>
        </div>
        {loginAlertMessage != "" && (
          <div className="col-12 ps-0">
            <Alert
              type={"error"}
              message={loginAlertMessage}
              closeAlert={() => {
                setLoginAlertMessage("");
              }}
            />
          </div>
        )}
        {resetPasswordInfoMessage != "" && (
          <div className="col-12 ps-0">
            <Alert
              type={"primary"}
              message={resetPasswordInfoMessage}
              closeAlert={() => {
                setResetPasswordInfoMessage("");
              }}
            />
          </div>
        )}
        <div
          className={`col-12 ${loginAlertMessage != "" || resetPasswordInfoMessage !== "" || loginFormOpen ? "mt-1" : "mt-3"} ps-0`}
        >
          <div className={`floating-label-wrap`}>
            <label htmlFor="email" className="label-checkout">
              {t("information.email")}
            </label>
            <input
              type="text"
              required
              className="floating-label-field floating-label-field--s1"
              id="email"
              disabled={doingRequest}
              placeholder={t("information.email")}
              onChange={(e) => setEmail(e.target.value.replace(" ", ""))}
              value={email}
            />
          </div>
        </div>
        <div className="col-12 mt-2 ps-0">
          <div className={`floating-label-wrap`}>
            <label htmlFor="name" className="label-checkout">
              {t("information.password")}
            </label>
            <input
              type="password"
              required
              className="floating-label-field floating-label-field--s1"
              id="password"
              disabled={doingRequest}
              placeholder={t("information.password")}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <div className="d-flex align-items-baseline">
          <div
            className="col-6 mt-3 label-checkout"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setLoginFormOpen(false);
            }}
          >
            <ArrowLeft size={24} weight="bold" />{" "}
            <span>{t("information.back-to-guest-checkout")}</span>
          </div>
          <div className="col-6 my-3">
            <button
              className="btn btn-primary py-3"
              disabled={doingRequest}
              onClick={() => {
                axios
                  .post(urls.login_at_checkout, {
                    params: {
                      customer: {
                        email: email,
                        password: password,
                      },
                    },
                  })
                  .then((response) => {
                    const response_json = response.data;
                    const responseType = response_json.type;
                    const responseMessage = response_json.message;

                    if (responseType == "error") {
                      setLoginAlertMessage(responseMessage);
                    } else {
                      setLoginFormOpen(false);
                      setLoginAlertMessage("");
                      dispatch(fetchDataInformation());
                    }
                  });
              }}
              id="save"
              style={{
                borderRadius: "8px",
                fontWeight: 700,
                backgroundColor: colors.main_color,
                color: colors.main_buttons_text_color,
              }}
            >
              {doingRequest ? (
                <LoadingIcon isInsideButton={true} />
              ) : (
                t("information.login")
              )}
            </button>
          </div>
        </div>
      </Fragment>
    );
  };

  const RenderPasswordForm = () => {
    return (
      <Fragment>
        <div className="col-12 ps-0">
          <div className="label-checkout" style={{ paddingBottom: "24px" }}>
            {t("information.checkout-as-guest")}
          </div>
        </div>
        {resetPasswordErrorMessage != "" && (
          <div className="col-12 ps-0">
            <Alert
              type={"error"}
              message={resetPasswordErrorMessage}
              closeAlert={() => {
                setResetPasswordErrorMessage("");
              }}
            />
          </div>
        )}
        <div
          className={`col-12 ${resetPasswordErrorMessage != "" ? "mt-1" : "mt-3"} ps-0`}
        >
          <div className={`floating-label-wrap`}>
            <label htmlFor="email" className="label-checkout">
              {t("information.email")}
            </label>
            <input
              type="text"
              required
              className="floating-label-field floating-label-field--s1"
              id="email"
              disabled={doingRequest}
              placeholder={t("information.email")}
              onChange={(e) => setEmail(e.target.value.replace(" ", ""))}
              value={email}
            />
          </div>
        </div>
        <div className="row align-items-baseline">
          <div
            className="col-6 mt-3 label-checkout"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setPasswordFormOpen(false);
            }}
          >
            <ArrowLeft /> {t("information.back-to-guest-checkout")}
          </div>
          <div className="col-6 my-3">
            <button
              className="btn btn-primary py-3"
              disabled={doingRequest}
              onClick={() => {
                axios
                  .get(urls.reset_password, {
                    params: {
                      customer: {
                        email: email,
                      },
                    },
                  })
                  .then((response) => {
                    const responseType = response.data.type;
                    const responseMessage = response.data.message;
                    if (responseType == "error") {
                      setResetPasswordInfoMessage("");
                      setResetPasswordErrorMessage(responseMessage);
                    } else {
                      setLoginAlertMessage("");
                      setResetPasswordErrorMessage("");
                      setPasswordFormOpen(false);
                      setLoginFormOpen(true);
                      setResetPasswordInfoMessage(responseMessage);
                    }
                  });
              }}
              id="reset"
              style={{
                backgroundColor: colors.main_color,
                color: colors.main_buttons_text_color,
              }}
            >
              {doingRequest ? (
                <LoadingIcon isInsideButton={true} />
              ) : (
                t("information.reset-button")
              )}
            </button>
          </div>
        </div>
      </Fragment>
    );
  };
  const AcceptsMarketing = (CustomField) => {
    const elementId = `custom_field_${CustomField.area}_${CustomField.custom_field_id}`;
    return (
      <div className="check col-12 mb-3 form-group">
        <label>
          <input
            type="checkbox"
            className="me-2 checkbox align-middle"
            disabled={doingRequest}
            id="accepts_marketing"
            onChange={(e) => {
              changeStoreField("accepts_marketing", e.target.checked);
              setCustomField(
                elementId,
                CustomField.custom_field_id,
                e.target.checked,
                CustomField.area,
              );
            }}
            checked={information.accepts_marketing}
          />
          <span
            className="align-middle"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                t(information.accepts_marketing_text) ||
                  t("information.accepts-marketing-text-default"),
                { ADD_ATTR: ["target"] },
              ),
            }}
          />
          <span></span>
        </label>
      </div>
    );
  };
  const PhoneNumber = () => {
    return (
      <div className="mb-n2">
        <PhoneInput
          isInformationContent={true}
          flagId={"prefix"}
          flagValue={getPhonePrefixCountry(currentPrefix, information.country)}
          flagPlaceholder={t("information.phone-code")}
          onFlagChange={(e) => {
            setCurrentPrefix(e.value);
          }}
          phoneValue={currentPhone || ""}
          phoneId={"phone"}
          phonePlaceholder={
            system_fields.phone_required
              ? t("information.phone") + " *"
              : t("information.phone-optional")
          }
          onNumberChange={(number) => {
            setCurrentPhone(number.replace(/ /g, ""));
          }}
          phoneError={t(phoneError.message)}
          phoneEmpty={t(information.errors.phone)}
          storeCountry={information.order_country}
        />
      </div>
    );
  };

  const ContactInformation = (
    <Fragment>
      <div
        className={`col-12 ${
          system_fields.accepts_marketing
            ? ""
            : information.custom_fields.contact.length > 0
              ? "mb-3"
              : "mb-4"
        }`}
      >
        {information.is_mobile && <MobileDiscountForm />}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "24px",
          }}
          className="checkout-section-title"
        >
          <div className="d-inline-flex align-items-center">
            {props.vertical && (
              <span
                style={{ color: colors.main_color_dark ? "white" : "black" }}
                className="pageNumber"
              >
                1
              </span>
            )}{" "}
            {t("information.contact")}
          </div>
          {information.customers_enabled &&
            !information.logged_in &&
            !loginFormOpen &&
            !passwordFormOpen && (
              <div
                onClick={() => {
                  setPasswordFormOpen(false);
                  setLoginFormOpen(true);
                }}
                id="login"
                className="checkout-sub-section-title fw-normal"
                style={{
                  color: colors.text_color,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <UserCircle /> {t("information.login")}
              </div>
            )}
        </div>

        {information.logged_in ? (
          <Fragment>
            <div className="logged-in-div">
              <div className="pe-2">
                <UserCircle size={24} weight="bold" />
              </div>
              <span id="filledEmail">{information.email}</span>
              <a
                href={`${urls.logout}?id=${information.customer_id}`}
                id="logout"
                className="small"
                style={{ color: "#33a2db" }}
              >
                {t("information.logout")}
              </a>
            </div>
          </Fragment>
        ) : loginFormOpen ? (
          RenderLoginForm()
        ) : passwordFormOpen ? (
          RenderPasswordForm()
        ) : (
          <div className="contact-information-box mb-3">
            <div className="checkout-guest-div">
              <div
                className={`form-group floating-label-wrap${
                  information.errors.email ? " error-div-highlight" : ""
                }`}
              >
                <MailCheck
                  email={information.email || ""}
                  domains={["hotmail.com", "gmail.com", "aol.com"]}
                  topLevelDomains={[
                    "com",
                    "net",
                    "org",
                    "edu",
                    "gov",
                    "mil",
                    "int",
                    "co",
                    "biz",
                    "info",
                    "pro",
                    "name",
                    "me",
                    "mobi",
                    "tv",
                    "asia", // Newer TLDs
                    "io",
                    "tech",
                    "dev",
                    "app",
                    "ai", // Tech-related TLDs

                    // Common country code TLDs
                    "us",
                    "ca",
                    "uk",
                    "de",
                    "fr",
                    "es",
                    "it",
                    "nl",
                    "se",
                    "no",
                    "fi",
                    "pl",
                    "ru", // European countries
                    "au",
                    "nz",
                    "jp",
                    "cn",
                    "kr",
                    "sg",
                    "in", // Asia-Pacific region
                    "br",
                    "mx",
                    "ar",
                    "cl",
                    "co",
                    "pe", // Latin America
                    "za",
                    "ng",
                    "eg", // Africa

                    // Some less common TLDs that still might be useful
                    "xyz",
                    "club",
                    "guru",
                    "online",
                    "site",
                    "solutions",
                    "store",
                    "world",
                    "live",
                  ]}
                >
                  {(suggestion) => (
                    <div>
                      <label htmlFor="email" className="label-checkout">
                        {t("information.email")} *
                      </label>
                      <input
                        type="text"
                        required
                        className="floating-label-field floating-label-field--s1"
                        name="email"
                        id="email"
                        disabled={doingRequest}
                        placeholder={t("information.email")}
                        onChange={(e) => {
                          dispatchSaveEmail(e.target.value.replace(" ", ""));
                          changeStoreField(
                            "email",
                            e.target.value.replace(" ", ""),
                          );
                        }}
                        value={
                          (information.email != null && information.email) || ""
                        }
                        autoComplete="email"
                      />

                      {suggestion && (
                        <div className="mb-3">
                          <span className="form-text text-muted email-suggestion">
                            {t("information.did-you-mean")}
                            {
                              <a
                                href="#"
                                onClick={() =>
                                  changeStoreField("email", suggestion.full)
                                }
                                style={{ color: colors.main_color }}
                              >
                                {suggestion.full}
                              </a>
                            }
                            ?
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </MailCheck>
              </div>
              <div
                className={`field-error ${information.errors.email ? "" : "hidden-error"}`}
                style={{
                  display: information.errors.email ? "flex" : "",
                  alignItems: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  style={{ paddingRight: "2px" }}
                  size="sm"
                  className="m-1"
                  fixedWidth
                />
                {t(information.errors.email)}
              </div>
            </div>
          </div>
        )}
        <div className="col-12">
          <div className="row mx-n2">
            {information.custom_fields.contact.length > 0 ? (
              <CustomFieldList
                customFields={information.custom_fields.contact}
                errors={information.errors}
                setCustomField={setCustomField}
                custom={2}
                PhoneNumber={system_fields.phone_enabled ? PhoneNumber : null}
                AcceptsMarketing={
                  system_fields.accepts_marketing ? AcceptsMarketing : null
                }
                locale={information.locale}
              />
            ) : null}
          </div>
        </div>
      </div>
    </Fragment>
  );

  const ShippingOptions = (
    <div className="col-12 mb-1 mt-3">
      <div className="checkout-section-title" style={{ paddingBottom: "24px" }}>
        <div className="d-inline-flex align-items-center">
          {props.vertical && (
            <span
              style={{ color: colors.main_color_dark ? "white" : "black" }}
              className="pageNumber"
            >
              2
            </span>
          )}
          {t("information.delivery")}
        </div>
      </div>
      <div className="shipping-options-div row g-0 mb-2">
        <div className="col-12">
          <div
            tabIndex="0"
            onKeyDown={(event) => handleKeyDown(event, "radio_delivery")}
            className={`delivery-div-vertical ${
              information.shipping_option === "delivery"
                ? ""
                : "disabled-shipping-option-up"
            }`}
            style={{
              height: "71px",
              backgroundColor:
                information.shipping_option === "delivery"
                  ? `#FCFCFC`
                  : `#FFFFFF`,
              border:
                information.shipping_option === "delivery"
                  ? mainColorBorder
                  : ``,
              overflow: "hidden",
              color: "#505050",
            }}
          >
            <label className="paymentRadioLabel" style={{ height: "71px" }}>
              <input
                className="radioOp"
                style={{ accentColor: colors.main_color, colorScheme: "light" }}
                type="radio"
                value="delivery"
                disabled={doingRequest}
                id="radio_delivery"
                onChange={(e) =>
                  changeShippingOption("shipping_option", e.target.value)
                }
                checked={information.shipping_option === "delivery"}
              />
              <div className="col-md-9 col-sm-8" style={{ lineHeight: "17px" }}>
                <div className="px-2">
                  <span style={{ fontWeight: 600 }} className="d-block">
                    {t("information.shipping")}
                  </span>
                  <span style={{ fontWeight: 400 }} className="d-block">
                    {t("information.delivery-info-text-vertical")}
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>
        {shipping.pickup_points && shipping.pickup_points.length > 0 && (
          <div className="col-12">
            <div
              tabIndex="0"
              onKeyDown={(event) => handleKeyDown(event, "radio_store_pickup")}
              className={`pickup-div-vertical ${
                information.shipping_option === "store_pickup"
                  ? ""
                  : "disabled-shipping-option"
              }`}
              style={{
                height: "71px",
                backgroundColor:
                  information.shipping_option === "store_pickup"
                    ? `#FCFCFC`
                    : `#FFFFFF`,
                border:
                  information.shipping_option === "store_pickup"
                    ? mainColorBorder
                    : ``,
                overflow: "hidden",
                borderBottomLeftRadius: information.new_pickup_map
                  ? "0px"
                  : "10px",
                borderBottomRightRadius: information.new_pickup_map
                  ? "0px"
                  : "10px",
              }}
            >
              <label className="paymentRadioLabel" style={{ height: "71px" }}>
                <input
                  className="radioOp"
                  style={{
                    accentColor: colors.main_color,
                    colorScheme: "light",
                  }}
                  type="radio"
                  value="store_pickup"
                  disabled={doingRequest}
                  id="radio_store_pickup"
                  onChange={(e) =>
                    changeShippingOption("shipping_option", e.target.value)
                  }
                  checked={information.shipping_option === "store_pickup"}
                />
                <div
                  className="shipping-option-text col-md-9 col-sm-8"
                  style={{ lineHeight: "17px" }}
                >
                  <div className="px-2" style={{ color: "#505050" }}>
                    <span style={{ fontWeight: 600 }} className="d-block">
                      {t("information.store-pick-up")}
                    </span>
                    <span style={{ fontWeight: 400 }} className="d-block">
                      {t("information.pickup-info-text")}
                    </span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}
        {information.new_pickup_map && (
          <div className="col-12">
            <div
              tabIndex="0"
              onKeyDown={(event) => handleKeyDown(event, "radio_point_pickup")}
              className={`point-pickup-div-vertical ${
                information.shipping_option === "point_pickup"
                  ? ""
                  : "disabled-shipping-option-down"
              }`}
              style={{
                height: "71px",
                backgroundColor:
                  information.shipping_option === "point_pickup"
                    ? `#FCFCFC`
                    : `#FFFFFF`,
                border:
                  information.shipping_option === "point_pickup"
                    ? mainColorBorder
                    : ``,
                overflow: "hidden",
              }}
            >
              <label className="paymentRadioLabel" style={{ height: "71px" }}>
                <input
                  className="radioOp"
                  style={{
                    accentColor: colors.main_color,
                    colorScheme: "light",
                  }}
                  type="radio"
                  value="point_pickup"
                  disabled={doingRequest}
                  id="radio_point_pickup"
                  onChange={(e) =>
                    changeShippingOption("shipping_option", e.target.value)
                  }
                  checked={information.shipping_option === "point_pickup"}
                />
                <div
                  className="shipping-option-text col-md-9 col-sm-8"
                  style={{ lineHeight: "17px" }}
                >
                  <div className="px-2" style={{ color: "#505050" }}>
                    <span style={{ fontWeight: 600 }} className="d-block">
                      {t("information.point-pick-up")}
                    </span>
                    <span style={{ fontWeight: 400 }} className="d-block">
                      {t("information.point-pickup-info-text")}
                    </span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const shippingHeader = (
    <div className="col-12 mb-1 mt-3">
      <div className="checkout-section-title">
        <div className="d-inline-flex align-items-center">
          {props.vertical && (
            <span
              style={{ color: colors.main_color_dark ? "white" : "black" }}
              className="pageNumber"
            >
              2
            </span>
          )}
          {t("information.delivery")}
        </div>
      </div>
    </div>
  );

  const ChoosePlace = (
    <Fragment>
      <div className="col-12">
        <div
          className="checkout-sub-section-title"
          style={{ paddingBottom: "16px", paddingTop: "16px" }}
        >
          {t("shipping.choose-place")}
        </div>
      </div>
      <div className="col-12">
        <div
          style={{
            borderTop: contentExceedsMaxHeight ? "1px solid #ccc" : "none",
            borderBottom: contentExceedsMaxHeight ? "1px solid #ccc" : "none",
          }}
        >
          {pickupLocationsHaveCoordinates &&
            information.shipping_option === "store_pickup" && (
              <div
                style={{
                  borderRadius: "10px 10px 0 0",
                  borderWidth: "1px 1px 0 1px",
                  borderColor: defaultColor,
                  overflow: "hidden",
                }}
              >
                <PickupLocationsMap
                  pickupPoints={shipping.pickup_points}
                  colors={colors}
                />
              </div>
            )}
          <div
            id="pickUpContainer"
            className={`shippingBox w-100 ${
              information.errors.pick_up != "" ? "error-div-highlight" : ""
            }`}
            style={{
              maxHeight: "628px",
              overflowY: contentExceedsMaxHeight ? "scroll" : "",
            }}
          >
            {shipping.pickup_points &&
              shipping.pickup_points.map((pickup_point, i) => {
                const selected_method =
                  shipping.pickup_point_id == pickup_point.id;
                const selectedMethodIndex = shipping.pickup_points.findIndex(
                  (method) => method.id === shipping.pickup_point_id,
                );
                const isNextMethod = i === selectedMethodIndex + 1;

                const methodOptionClass =
                  shipping.pickup_points.length === 1 &&
                  !pickupLocationsHaveCoordinates
                    ? "10px 10px 10px 10px"
                    : (shipping.pickup_points.length === 1 &&
                          pickupLocationsHaveCoordinates) ||
                        i == shipping.pickup_points.length - 1
                      ? "0 0 10px 10px"
                      : shipping.pickup_points.length > 1 &&
                          i === 0 &&
                          !pickupLocationsHaveCoordinates
                        ? "10px 10px 0 0"
                        : "";
                return (
                  <div
                    tabIndex="0"
                    onKeyDown={(event) =>
                      handleKeyDown(
                        event,
                        "radio_pickup_point_" + pickup_point.id,
                      )
                    }
                    className="pickup-names-div"
                    key={pickup_point.id}
                    data-tooltip-id={"full-stock-needed-" + pickup_point.id}
                    style={{
                      borderTop: selected_method
                        ? mainColorBorder
                        : isNextMethod
                          ? ""
                          : defaultColorBorder,
                      borderLeft: selected_method
                        ? mainColorBorder
                        : defaultColorBorder,
                      borderRight: selected_method
                        ? mainColorBorder
                        : defaultColorBorder,
                      borderBottom: selected_method
                        ? mainColorBorder
                        : shipping.pickup_points.length === i + 1 &&
                            !selected_method
                          ? defaultColorBorder
                          : "",
                      borderRadius: methodOptionClass,
                      color: "#505050",
                    }}
                  >
                    <label
                      style={{
                        borderRadius: `${methodOptionClass}`,
                        backgroundColor:
                          shipping.pickup_point_id == pickup_point.id
                            ? "#FCFCFC"
                            : "#FFFFFF",
                      }}
                      className="radioPickupLabel"
                      htmlFor={"radio_pickup_point_" + pickup_point.id}
                    >
                      <div className="d-flex flex-nowarp">
                        <input
                          className="radioOp"
                          style={{
                            accentColor: colors.main_color,
                            colorScheme: "light",
                          }}
                          type="radio"
                          disabled={
                            doingRequest ||
                            !pickup_point.location_can_fulfill_order
                          }
                          id={"radio_pickup_point_" + pickup_point.id}
                          value={pickup_point.id}
                          checked={
                            shipping.pickup_point_id == pickup_point.id ||
                            shipping.pickup_points.length === 1
                          }
                          onChange={(e) =>
                            changePickupPointFields(
                              parseInt(e.target.value, 10),
                              pickup_point.name,
                            )
                          }
                        />
                        <div
                          className="py-sm-0 py-2 px-2"
                          style={{ color: "#505050" }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                            }}
                            className="d-block pb-1"
                          >
                            {pickup_point.name}
                          </span>
                          <span className="subtext d-block pb-1">
                            {[
                              pickup_point.address.address,
                              pickup_point.address.complement,
                              pickup_point.address.municipality,
                              pickup_point.address.region,
                              pickup_point.address.country,
                            ]
                              .filter((x) => x)
                              .join(", ")}
                          </span>
                          {!pickup_point.location_can_fulfill_order && (
                            <span className="subtext d-block pb-1">
                              {t("information.full-stock-needed")}{" "}
                              {pickup_point.location_missing_products && (
                                <u
                                  onClick={() =>
                                    setPickupMissingProductsOpen((prev) =>
                                      prev.includes(pickup_point.id)
                                        ? prev.filter(
                                            (existingId) =>
                                              existingId !== pickup_point.id,
                                          )
                                        : [...prev, pickup_point.id],
                                    )
                                  }
                                >
                                  {pickupMissingProductsOpen.includes(
                                    pickup_point.id,
                                  )
                                    ? t("information.close-products")
                                    : t("information.view-products")}
                                </u>
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      {pickup_point.location_missing_products &&
                        pickupMissingProductsOpen.includes(pickup_point.id) && (
                          <ul className="ps-5 mb-0 pb-1 w-100">
                            {pickup_point.location_missing_products.map(
                              (product) => (
                                <li
                                  key={
                                    product.product_id +
                                    "-" +
                                    product.product_option_property_id
                                  }
                                >
                                  {product.product_name}
                                </li>
                              ),
                            )}
                          </ul>
                        )}
                    </label>
                    {shipping.pickup_point_id == pickup_point.id &&
                      pickup_point.description && (
                        <div
                          style={{
                            backgroundColor: "white",
                            borderTop:
                              shipping.pickup_point_id == pickup_point.id
                                ? "1px solid #F5F5F5"
                                : "",
                          }}
                          className={`pickup_point_div ${
                            i === shipping.pickup_points.length - 1
                              ? "methodOptionRoundedLabelDown"
                              : ""
                          }`}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                pickup_point.description,
                              ),
                            }}
                          ></span>
                        </div>
                      )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </Fragment>
  );

  const ChooseBranch = (
    <Fragment>
      <div className="col-12">
        <div
          className="checkout-sub-section-title"
          style={{ paddingBottom: "16px", paddingTop: "16px" }}
        >
          {t("shipping.choose-place")}
        </div>
      </div>
      <div className="col-12">
        <div
          style={{
            border: `1px solid ${defaultColor}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "20px",
              paddingBottom: "0px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <AddressSearchInput
              onSearch={(value) => {
                dispatch(fetchAddressForBranchAddress(value));
              }}
              information={information}
              colors={colors}
              callUpdateRegion={callUpdateRegion}
              style={{ width: "100%" }}
            />
          </div>
          {((information.region && information.municipality) ||
            shipping.address_for_branch_search?.address) &&
            loadedShippingBranches?.length > 0 && (
              <div
                style={{
                  borderTop: `1px solid ${defaultColor}`,
                  overflow: "hidden",
                }}
              >
                <PickupPointsMap
                  pickupPoints={loadedShippingBranches}
                  colors={colors}
                />
              </div>
            )}

          {((information.region && information.municipality) ||
            shipping.address_for_branch_search?.address) &&
            loadedShippingBranches?.length > 0 && (
              <div
                id="pickUpContainer"
                ref={containerRef}
                className={`pickupUpContainer w-100 `}
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  borderRadius: "0 0 10px 10px",
                  backgroundClip: "padding-box",
                }}
              >
                {shipping.branches &&
                  shipping.branches.map((branch, i) => {
                    const selected_branch = shipping.branch_id == branch.value;

                    const priceObj = shipping.prices_per_service?.find(
                      (s) => s.service_id === selectedValue?.original.id,
                    );

                    return (
                      <div
                        key={branch.value}
                        ref={(el) => {
                          if (el) branchRefs.current[branch.value] = el;
                          else delete branchRefs.current[branch.value];
                        }}
                        tabIndex="0"
                        onKeyDown={(event) =>
                          handleKeyDown(
                            event,
                            "radio_point_pickup_" + branch.value,
                          )
                        }
                        onClick={(event) => {
                          if (event.target.tagName !== "INPUT") {
                            changeBranchFields(
                              branch.value,
                              branch.name,
                              branch.address,
                              i,
                              branch,
                            );
                          }
                        }}
                        className="pickup-names-div"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          borderTop: defaultColorBorder,
                          color: "#505050",
                        }}
                      >
                        <label
                          style={{
                            backgroundColor:
                              shipping.branch_id == branch.value
                                ? "#FCFCFC"
                                : "#FFFFFF",
                          }}
                          className="radioPickupLabel flex-nowrap"
                          htmlFor={"radio_pickup_point_" + branch.value}
                        >
                          <input
                            className="radioOp"
                            style={{
                              accentColor: colors.main_color,
                              colorScheme: "light",
                            }}
                            type="radio"
                            disabled={doingRequest}
                            id={"radio_point_pickup" + branch.value}
                            value={branch.value}
                            checked={
                              shipping.branch_id == branch.value ||
                              shipping.branches.length === 1
                            }
                            onChange={(e) =>
                              changeBranchFields(
                                parseInt(e.target.value, 10),
                                branch.name,
                                branch.address,
                                i,
                                branch,
                              )
                            }
                          />
                          <div className="row px-2" style={{ width: "100%" }}>
                            <div
                              className="col-12 py-sm-0 py-2 px-3"
                              style={{
                                color: "#505050",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <div>
                                <span
                                  style={{ fontWeight: 600 }}
                                  className="d-block"
                                >
                                  {branch.name}
                                </span>
                                <span className="subtext d-block">
                                  {branch.address}
                                </span>
                              </div>

                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: colors.text_color,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                  padding: "0 10px",
                                  border: "none",
                                  marginLeft: "auto",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <span className="subtext d-block">
                                  {selected_branch ? (
                                    priceObj?.price === "-1" ? (
                                      "Servicio no disponible"
                                    ) : priceObj?.price === "" ? (
                                      <LoadingIcon />
                                    ) : (
                                      priceObj?.price
                                    )
                                  ) : (
                                    ""
                                  )}
                                </span>
                              </div>
                            </div>
                            {selected_branch && (
                              <div className="col-12 px-3">
                                <div
                                  className="checkout-sub-section-title py-1"
                                  style={{ color: colors.text_color }}
                                >
                                  {shipping.selected_branch?.service?.length < 2
                                    ? t("shipping.service")
                                    : t("shipping.select-service")}
                                </div>
                                <div
                                  style={{ zIndex: "9999" }}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <Select
                                    styles={customStyles(false)}
                                    classNamePrefix="custom-select"
                                    isSearchable
                                    id="service-options"
                                    options={serviceOptions}
                                    onChange={(e) => changeService(e.value)}
                                    value={selectedValue}
                                    isDisabled={
                                      shipping.selected_branch?.service
                                        ?.length < 2
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
              </div>
            )}
        </div>
      </div>
    </Fragment>
  );

  const AdditionalInformation = (
    <div className="col-12 mb-3 ">
      <div
        className={`floating-label-wrap ${
          information.errors.additional_information ? "error-div-highlight" : ""
        }`}
      >
        <label htmlFor="additional_information" className="label-checkout">
          {t("information.special-informations")}
        </label>
        <textarea
          className="floating-label-field floating-label-field--s1"
          id="additional_information"
          disabled={doingRequest}
          placeholder={t("information.special-informations")}
          onChange={(e) =>
            changeStoreField("additional_information", e.target.value)
          }
          value={
            (information != null && information.additional_information) || ""
          }
        ></textarea>
      </div>
      <div
        className={`field-error ${
          information.errors.additional_information ? "" : "hidden-error"
        }`}
        style={{
          display: information.errors.additional_information ? "flex" : "",
          alignItems: "center",
        }}
      >
        <FontAwesomeIcon
          icon={faCircleExclamation}
          style={{ paddingRight: "2px" }}
          size="sm"
          className="m-1"
          fixedWidth
        />
        {t(information.errors.additional_information)}
      </div>
    </div>
  );

  const PickUp = (
    <Fragment>
      {information.shipping_option === "store_pickup" && ChoosePlace}
      <div className="col-12 mt-4">
        <div className="separatedContent">
          <div
            className="checkout-sub-section-title"
            style={{ paddingBottom: "8px" }}
          >
            {t("information.who-picks-up-information")}
          </div>
        </div>
      </div>
      <div className="col-12">
        <div className="row">
          <div className="col-sm-6  pe-sm-1 mb-3">
            <div
              className={`floating-label-wrap ${
                information.errors.name ? "error-div-highlight" : ""
              }`}
            >
              <label htmlFor="name" className="label-checkout">
                {t("information.name")} *
              </label>
              <input
                type="text"
                required
                className="floating-label-field floating-label-field--s1"
                id="name"
                disabled={doingRequest}
                placeholder={t("information.name")}
                onChange={(e) => {
                  changeStoreField("name", e.target.value);
                  dispatch(setFieldBilling("name", e.target.value));
                }}
                value={(information != null && information.name) || ""}
                autoComplete="given-name"
              />
            </div>
            <div
              className={`field-error ${information.errors.name ? "" : "hidden-error"}`}
              style={{
                display: information.errors.name ? "flex" : "",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ paddingRight: "2px" }}
                size="sm"
                fixedWidth
              />
              {t(information.errors.name)}
            </div>
          </div>
          <div className="col-sm-6  ps-sm-1 mb-3">
            <div
              className={`floating-label-wrap ${
                information.errors.surname ? "error-div-highlight" : ""
              }`}
            >
              <label htmlFor="surname" className="label-checkout">
                {t("information.surname")} *
              </label>
              <input
                type="text"
                required
                className="floating-label-field floating-label-field--s1"
                id="surname"
                disabled={doingRequest}
                placeholder={t("information.surname")}
                onChange={(e) => {
                  changeStoreField("surname", e.target.value);
                  dispatch(setFieldBilling("surname", e.target.value));
                }}
                value={(information != null && information.surname) || ""}
                autoComplete="family-name"
              />
            </div>
            <div
              className={`field-error ${information.errors.surname ? "" : "hidden-error"}`}
              style={{
                display: information.errors.surname ? "flex" : "",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ paddingRight: "2px" }}
                size="sm"
                fixedWidth
              />
              {t(information.errors.surname)}
            </div>
          </div>
          {(information.countries.length === 1 && information.country) ||
          !(
            information.shipping_option === "store_pickup" &&
            information.show_billing_address_on_store_pickup === "0"
          ) ? null : (
            <div className="col-12  mb-3">
              <div
                className={`floating-label-wrap ${
                  information.errors.country ? "error-div-highlight" : ""
                }`}
              >
                <label htmlFor="country" className="label-checkout">
                  {t("information.country")} *
                </label>
                <div>
                  <Select
                    styles={customStyles(information.errors.country)}
                    classNamePrefix="custom-select"
                    searchable
                    id="country"
                    options={information.countries}
                    onChange={(row) => callUpdateCountry(row.value)}
                    value={
                      information.countries.find(
                        (current) => current.value === information.country,
                      ) || ""
                    }
                    autoComplete="country"
                  />
                </div>
              </div>
              <div
                className={`field-error ${
                  information.errors.country ? "" : "hidden-error"
                }`}
                style={{
                  display: information.errors.country ? "flex" : "",
                  alignItems: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  style={{ paddingRight: "2px" }}
                  size="sm"
                  fixedWidth
                />
                {t(information.errors.country)}
              </div>
            </div>
          )}
        </div>
      </div>

      {information.custom_fields.shipping.length > 0 ? (
        <CustomFieldList
          customFields={information.custom_fields.shipping}
          errors={information.errors}
          setCustomField={setCustomField}
          AdditionalInformation={
            system_fields.additional_information ? AdditionalInformation : null
          }
          locale={information.locale}
        />
      ) : null}
    </Fragment>
  );

  const NoPickupsAndNoShippingMethods = (
    <Fragment>
      <div className="col-12">
        <Alert
          message={t("information.need-pickup-or-shipping")}
          type={"error"}
        />
      </div>
    </Fragment>
  );

  const Digital = (
    <Fragment>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: "24px",
        }}
        className="checkout-section-title"
      >
        <div className="d-inline-flex align-items-center">
          {props.vertical && (
            <span
              style={{ color: colors.main_color_dark ? "white" : "black" }}
              className="pageNumber"
            >
              2
            </span>
          )}{" "}
          {t("information.billing")}
        </div>
      </div>
      <div className="col-12 mt-3">
        <div className="row">
          <div className="col-sm-6  pe-sm-1 mb-3">
            <div
              className={`floating-label-wrap ${
                information.errors.name ? "error-div-highlight" : ""
              }`}
            >
              <label htmlFor="name" className="label-checkout">
                {t("information.name")} *
              </label>
              <input
                type="text"
                required
                className="floating-label-field floating-label-field--s1"
                id="name"
                disabled={doingRequest}
                placeholder={t("information.name")}
                onChange={(e) => changeStoreField("name", e.target.value)}
                value={(information != null && information.name) || ""}
                autoComplete="given-name"
              />
            </div>
            <div
              className={`field-error ${information.errors.name ? "" : "hidden-error"}`}
              style={{
                display: information.errors.name ? "flex" : "",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ paddingRight: "2px" }}
                size="sm"
                fixedWidth
              />
              {t(information.errors.name)}
            </div>
          </div>
          <div className="col-sm-6  ps-sm-1 mb-3">
            <div
              className={`floating-label-wrap ${
                information.errors.surname ? "error-div-highlight" : ""
              }`}
            >
              <label htmlFor="surname" className="label-checkout">
                {t("information.surname")} *
              </label>
              <input
                type="text"
                required
                className="floating-label-field floating-label-field--s1"
                id="surname"
                disabled={doingRequest}
                placeholder={t("information.surname")}
                onChange={(e) => changeStoreField("surname", e.target.value)}
                value={(information != null && information.surname) || ""}
                autoComplete="family-name"
              />
            </div>
            <div
              className={`field-error ${information.errors.surname ? "" : "hidden-error"}`}
              style={{
                display: information.errors.surname ? "flex" : "",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ paddingRight: "2px" }}
                size="sm"
                fixedWidth
              />
              {t(information.errors.surname)}
            </div>
          </div>
          {information.countries.length === 1 && information.country ? null : (
            <div className="col-12  mb-3">
              <div
                className={`floating-label-wrap ${
                  information.errors.country ? "error-div-highlight" : ""
                }`}
              >
                <label htmlFor="country" className="label-checkout">
                  {t("information.country")} *
                </label>
                <div>
                  <Select
                    styles={customStyles(information.errors.country)}
                    classNamePrefix="custom-select"
                    searchable
                    id="country"
                    options={information.countries}
                    onChange={(row) => callUpdateCountry(row.value)}
                    value={
                      information.countries.find(
                        (current) => current.value === information.country,
                      ) || ""
                    }
                    autoComplete="country"
                  />
                </div>
              </div>
              <div
                className={`field-error ${
                  information.errors.country ? "" : "hidden-error"
                }`}
                style={{
                  display: information.errors.country ? "flex" : "",
                  alignItems: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  style={{ paddingRight: "2px" }}
                  size="sm"
                  fixedWidth
                />
                {t(information.errors.country)}
              </div>
            </div>
          )}
        </div>
      </div>

      {information.custom_fields.shipping.length > 0 ? (
        <CustomFieldList
          customFields={information.custom_fields.shipping}
          errors={information.errors}
          setCustomField={setCustomField}
          AdditionalInformation={
            system_fields.additional_information ? AdditionalInformation : null
          }
          locale={information.locale}
        />
      ) : null}
    </Fragment>
  );

  const PhysicalProducts = (
    <Fragment>
      {information.shipping_option === "delivery" && (
        <Fragment>
          <div className="col-12">
            <div className="separatedContent">
              <div
                className="checkout-sub-section-title"
                style={{ paddingBottom: "16px", paddingTop: "16px" }}
              >
                {t("information.shipping-address")}
              </div>
            </div>
          </div>
          <div className="col-12">
            <AddressForm
              address={information}
              countries={information.countries}
              regions={information.regions}
              municipalities={information.municipalities}
              customFields={information.custom_fields.shipping}
              type="shipping"
              no_shipping_required={information.no_shipping_required}
              request_geolocation={information.request_geolocation}
              show_estimate_dates={information.show_estimate_dates}
              phone={information.phone}
              phone_prefix={information.phone_prefix}
              order_country={information.order_country}
              errors={information.errors}
              setFieldInformation={changeStoreField}
              callUpdateLocations={callUpdateLocations}
              callUpdateCountry={callUpdateCountry}
              callUpdateRegion={callUpdateRegion}
              addressAutofill={addressAutofill}
              AdditionalInformation={AdditionalInformation}
              systemFields={system_fields}
              setCustomField={setCustomField}
              colors={colors}
              country_calling_codes={information.country_calling_codes}
              countries_that_use_neighbourhood={
                information.countries_that_use_neighbourhood
              }
              locale={information.locale}
            />
          </div>
        </Fragment>
      )}
      {shipping.pickup_points &&
        shipping.pickup_points.length > 0 &&
        information.shipping_option === "store_pickup" &&
        PickUp}
      {information.shipping_option === "point_pickup" && (
        <>
          {ChooseBranch}
          {PickUp}
        </>
      )}
    </Fragment>
  );

  if (props.vertical)
    return (
      <div id="information-page" className="row">
        {information.is_mobile && (
          <ShowOrderSummary
            is_mobile={information.is_mobile}
            type="information"
            toggleSummary={changeStoreField}
          />
        )}
        {alertMessage && alertMessage.active && (
          <div className="col-12" ref={errorMsgCallback}>
            <Alert
              message={alertMessage.message}
              type={alertMessage.type}
              closeAlert={() => dispatch(closeAlertMessage())}
            />
          </div>
        )}
        {information.info_msg && alertMessage && alertMessage.active && (
          <div className="col-12" ref={errorMsgCallback}>
            <Alert
              message={information.info_msg}
              type="notice"
              closeAlert={() => dispatch(closeAlertMessage())}
            />
          </div>
        )}
        {window.stripePubKey &&
          !information.have_custom_fields &&
          information.stripe_payment_express && (
            <StripeExpressCheckout
              stripePromise={props.stripePromise}
              summary={summary}
              information={information}
              shipping={shipping}
              urls={urls}
              colors={colors}
            />
          )}
        {shipping.pickup_points &&
          shipping.pickup_points.length == 0 &&
          information.shipping_option != "no_shipping" &&
          !information.has_shipping_methods &&
          NoPickupsAndNoShippingMethods}
        {ContactInformation}
        {!information.no_shipping_required &&
        ((shipping.pickup_points && shipping.pickup_points.length > 0) ||
          information.new_pickup_map) &&
        information.has_shipping_methods ? (
          ShippingOptions
        ) : information.shipping_option != "no_shipping" ? (
          shippingHeader
        ) : (
          <></>
        )}
        {!information.no_shipping_required && PhysicalProducts}
        {information.no_shipping_required &&
          information.shipping_option === "no_shipping" &&
          information.show_billing_address_on_digital_products !== "1" &&
          Digital}
        {information.custom_fields.other.length > 0 ? (
          <CustomFieldList
            customFields={information.custom_fields.other}
            errors={information.errors}
            setCustomField={setCustomField}
            AdditionalInformation={
              system_fields.additional_information
                ? AdditionalInformation
                : null
            }
            locale={information.locale}
          />
        ) : null}
      </div>
    );
  return (
    <div id="information-page" className="row">
      {information.is_mobile && (
        <ShowOrderSummary
          is_mobile={information.is_mobile}
          type="information"
          toggleSummary={changeStoreField}
        />
      )}
      <Header page="1" />
      {alertMessage && alertMessage.active && (
        <div className="col-12" ref={errorMsgCallback}>
          <Alert
            message={alertMessage.message}
            type={alertMessage.type}
            closeAlert={() => dispatch(closeAlertMessage())}
          />
        </div>
      )}
      {information.info_msg && alertMessage && alertMessage.active && (
        <div className="col-12" ref={errorMsgCallback}>
          <Alert
            message={information.info_msg}
            type="notice"
            closeAlert={() => dispatch(closeAlertMessage())}
          />
        </div>
      )}
      {window.stripePubKey &&
        !information.have_custom_fields &&
        information.stripe_payment_express && (
          <StripeExpressCheckout
            stripePromise={props.stripePromise}
            summary={summary}
            information={information}
            shipping={shipping}
            urls={urls}
            colors={colors}
          />
        )}
      {shipping.pickup_points &&
        shipping.pickup_points.length == 0 &&
        information.shipping_option != "no_shipping" &&
        !information.has_shipping_methods &&
        NoPickupsAndNoShippingMethods}
      {ContactInformation}
      {!information.no_shipping_required &&
        ((shipping.pickup_points && shipping.pickup_points.length > 0) ||
          information.new_pickup_map) &&
        information.has_shipping_methods &&
        ShippingOptions}
      {!information.no_shipping_required && PhysicalProducts}
      {information.no_shipping_required &&
        information.shipping_option === "no_shipping" &&
        information.show_billing_address_on_digital_products !== "1" &&
        Digital}
      {information.custom_fields.other.length > 0 ? (
        <CustomFieldList
          customFields={information.custom_fields.other}
          errors={information.errors}
          setCustomField={setCustomField}
          AdditionalInformation={
            system_fields.additional_information ? AdditionalInformation : null
          }
          locale={information.locale}
        />
      ) : null}
      <NavigationFooter type="information" saveData={saveData} />
    </div>
  );
};

export default InformationContent;
